import React, { useEffect, useState } from 'react';
import { PopUpSlider } from 'geniebook-ui/dist';
import { useNavigate, useParams } from 'react-router-dom';
import {
  subjectIdFromName,
  subjectRouteFromId,
} from '@utils/helpers/SubjectParser';
import { useAtom } from 'jotai';
import {
  STATE_ONBOARDING_MASTERY_LEVEL_FINISHED,
  TOUR_ONBOARDING_FLAG,
  TOUR_ONBOARDING_MASTERY_LEVEL_FLAG,
} from '@utils/constants/tour.constants';
import {
  useGetStudentOnboardingMasteryLevelPermission,
  useIsJCUser,
} from '@hooks/user.hook';
import Flickity from 'react-flickity-component';
import { CDN_URL } from '@utils/constants/url.constants';
import { stateOnboardingTooltipAtom } from '@atom/worksheetAtom';
import { delay } from '@utils/helpers/timer';
import { MENU_PAGE_PROGRESS } from '@utils/constants/menu.constants';
import { useSubjectMenuList } from '@hooks/student.hook';

// Types for the responsive image config
type ImageDimensions = {
  src: string;
  dimensions: string;
  alt: string;
};

type ImageConfig = {
  desktop: ImageDimensions;
  mobile: ImageDimensions;
};

// type for the entire image config object
type SkillLevelImages = {
  [key: string]: ImageConfig;
};

type JCSkillLevelConfig = {
  desktop: ImageDimensions;
  title: string;
  desc: string;
};

type JCSkillLevelItems = {
  [key: string]: JCSkillLevelConfig;
};

// images on Skill Level Popup
const SKILL_LEVEL_IMAGES: SkillLevelImages = {
  image1: {
    desktop: {
      src: `${CDN_URL}assets/images/mastery/Desktop-Tablet/Skill Level Popup_Web_1.svg`,
      dimensions: 'w-[405px] h-[294px]',
      alt: 'skill_level_popup_web1',
    },
    mobile: {
      src: `${CDN_URL}assets/images/mastery/Mobile/Skill Level Popup_Mob_1.svg`,
      dimensions: 'w-[262px] h-[313px]',
      alt: 'skill_level_popup_mob1',
    },
  },
  image2: {
    desktop: {
      src: `${CDN_URL}assets/images/mastery/Desktop-Tablet/Skill Level Popup_Web_2.svg`,
      dimensions: 'w-[405px] h-[294px]',
      alt: 'skill_level_popup_web2',
    },
    mobile: {
      src: `${CDN_URL}assets/images/mastery/Mobile/Skill Level Popup_Mob_2.svg`,
      dimensions: 'w-[262px] h-[313px]',
      alt: 'skill_level_popup_mob2',
    },
  },
  image3: {
    desktop: {
      src: `${CDN_URL}assets/images/mastery/Desktop-Tablet/Skill Level Popup_Web_3.svg`,
      dimensions: 'w-[405px] h-[294px]',
      alt: 'skill_level_popup_web3',
    },
    mobile: {
      src: `${CDN_URL}assets/images/mastery/Mobile/Skill Level Popup_Mob_3.svg`,
      dimensions: 'w-[262px] h-[313px]',
      alt: 'skill_level_popup_mob3',
    },
  },
  image4: {
    desktop: {
      src: `${CDN_URL}assets/images/mastery/Desktop-Tablet/Skill Level Popup_Web_4.svg`,
      dimensions: 'w-[405px] h-[294px]',
      alt: 'skill_level_popup_web4',
    },
    mobile: {
      src: `${CDN_URL}assets/images/mastery/Mobile/Skill Level Popup_Mob_4.svg`,
      dimensions: 'w-[262px] h-[313px]',
      alt: 'skill_level_popup_mob4',
    },
  },
};

const JC_SKILL_LEVEL_IMAGES: JCSkillLevelItems = {
  image1: {
    desktop: {
      src: `${CDN_URL}assets/images/mastery/Desktop-Tablet/JC Skill Level Popup_Web_1.svg`,
      dimensions: 'w-full h-full',
      alt: 'skill_level_popup_web1',
    },
    title: 'Introducing skill levels!',
    desc: 'Skill levels are a new way to track your progress and improvements!',
  },
  image2: {
    desktop: {
      src: `${CDN_URL}assets/images/mastery/Desktop-Tablet/JC Skill Level Popup_Web_2.svg`,
      dimensions: 'w-full h-full',
      alt: 'skill_level_popup_web2',
    },
    title: 'Detailed tracking for subjects',
    desc: 'With skill levels, you can measure your proficiency in each subject & topic.',
  },
  image3: {
    desktop: {
      src: `${CDN_URL}assets/images/mastery/Desktop-Tablet/JC Skill Level Popup_Web_3.svg`,
      dimensions: 'w-full h-full',
      alt: 'skill_level_popup_web3',
    },
    title: 'Work your way up to Level 10',
    desc: 'Start from Level 1 and ascend to the stars by practising questions every day!',
  },
  image4: {
    desktop: {
      src: `${CDN_URL}assets/images/mastery/Desktop-Tablet/JC Skill Level Popup_Web_4.svg`,
      dimensions: 'w-full h-full',
      alt: 'skill_level_popup_web4',
    },
    title: "Don't let your skill levels drop!",
    desc: 'Skill levels for each topic drop after 6 months of no activity. Check which skills need refreshing!',
  },
};

// reusable component for Skill Level Popup
const MasteryLevelPopupImages = ({
  src,
  dimensions,
  alt,
}: ImageDimensions & { alt: string }) => (
  <img src={src} className={dimensions} alt={alt} />
);

const PopupSliderMasteryLevel = () => {
  const params = useParams();
  const { isJC } = useIsJCUser({
    subjectId: subjectIdFromName(params.subjectId || ''),
  });
  const subjectId = subjectIdFromName(params.subjectId || '');
  const subjectRoute = subjectId
    ? subjectRouteFromId(subjectId)
    : 'mathematics';
  const navigate = useNavigate();
  const breakpoint = require('geniebook-ui/dist/themes/breakpoint');
  const [viewWidth, setViewWidth] = useState<number>(0);
  const [flickity, setFlickity] = useState<Flickity>();

  const [stateOnboardingTooltip, setStateOnboardingTooltip] = useAtom(
    stateOnboardingTooltipAtom,
  );
  useEffect(() => {
    window.addEventListener('resize', () => setViewWidth(window.innerWidth));
    setViewWidth(window.innerWidth);
    return () => {
      window.removeEventListener('resize', () =>
        setViewWidth(window.innerWidth),
      );
    };
  }, []);

  const [showOnboardingMasteryPopup, setShowOnboardingMasteryPopup] =
    useState<boolean>(false);
  const studentOnboardingMasteryLevelPermission =
    useGetStudentOnboardingMasteryLevelPermission();

  useEffect(() => {
    try {
      // if allowed to show , set popup as true
      if (
        studentOnboardingMasteryLevelPermission.data?.data?.data
          ?.is_show_onboarding_mastery_level
      ) {
        setShowOnboardingMasteryPopup(true);
      }
    } catch (error) {
      console.error('Unknown Error ', error);
    }
  }, [studentOnboardingMasteryLevelPermission.data]);

  const navigateToProgress = () => {
    setShowOnboardingMasteryPopup(false);
    localStorage.setItem(TOUR_ONBOARDING_MASTERY_LEVEL_FLAG, '1');
    setStateOnboardingTooltip(STATE_ONBOARDING_MASTERY_LEVEL_FINISHED);
    navigate(`/${subjectRoute}/progress`);
  };

  useEffect(() => {
    checkShowOnboardingMasteryPopup();
  }, []);

  const checkShowOnboardingMasteryPopup = () => {
    // onboarding mastery level tutorial check
    const onboardingMasteryLevelFlag = localStorage.getItem(
      TOUR_ONBOARDING_MASTERY_LEVEL_FLAG,
    );

    // if student haven't click all onboarding mastery level flag and first time login -> mutate check permission, if allowed -> show the popup
    if (onboardingMasteryLevelFlag != '1') {
      studentOnboardingMasteryLevelPermission.mutate();
    }
  };

  // Create a delay for a second. to fix bug slider transitioned before fully loaded
  const promise = delay(1000);

  useEffect(() => {
    if (flickity) {
      promise.then(() => {
        flickity.resize();
      });
    }
  }, [flickity]);

  // GBS-1937 prevent popup when student has no access to the progress page since the last popup redirects to it
  const [hasAccessProgress, setHasAccessProgress] = useState(false);
  const querySubjectMenuList = useSubjectMenuList();

  useEffect(() => {
    if (!params.subjectId || querySubjectMenuList.data?.data.error) return;

    const subjectDbId = subjectIdFromName(params.subjectId);

    const subjectMenu = querySubjectMenuList.data?.data?.data?.find(
      subject => parseInt(subject.subject_id) == subjectDbId,
    );

    if (subjectMenu && subjectMenu.menu_list.includes(MENU_PAGE_PROGRESS))
      setHasAccessProgress(true);
  }, [params.subjectId, querySubjectMenuList.data?.data]);

  const getResponsiveImageConfig = (
    imageConfig: ImageConfig,
  ): ImageDimensions => {
    const isDesktop = viewWidth > breakpoint.md;
    return isDesktop ? imageConfig.desktop : imageConfig.mobile;
  };

  const getJCuserConfig = (item: JCSkillLevelConfig) => {
    return {
      imageItem: item.desktop,
      title: item.title,
      desc: item.desc,
    };
  };

  const popupSliderData = Object.values(SKILL_LEVEL_IMAGES).map(itemConfig => ({
    content: (
      <div>
        <MasteryLevelPopupImages {...getResponsiveImageConfig(itemConfig)} />
      </div>
    ),
  }));

  const JCPopupSliderData = Object.values(JC_SKILL_LEVEL_IMAGES).map(item => {
    const config = getJCuserConfig(item);
    return {
      content: (
        <div>
          <MasteryLevelPopupImages {...config.imageItem} />
          <main className="flex flex-col gap-2">
            <section className="mastery-popup-title text-primary-navyblue font-black text-2xl">
              {config.title}
            </section>
            <p className="font-light">{config.desc}</p>
          </main>
        </div>
      ),
    };
  });

  // onboarding mastery popup only can appear after onboarding tooltip finished
  const showPopup =
    showOnboardingMasteryPopup &&
    hasAccessProgress &&
    (localStorage.getItem(TOUR_ONBOARDING_FLAG) == '1' ||
      stateOnboardingTooltip == 1);

  return showPopup ? (
    <PopUpSlider
      onMount={ref => setFlickity(ref)}
      popupContainerClassnames={`w-[469px] max-md:w-[326px] max-md:h-[520px] p-8 max-md:pb-4 ${
        isJC
          ? 'h-[525px] [&>div.carousel>div.flickity-viewport]:!h-[320px] [&>div.carousel]:!mb-5'
          : 'h-[498px]'
      }`}
      sliderOptions={{
        pageDots: true,
        contain: true,
        imagesLoaded: true,
      }}
      data={isJC ? JCPopupSliderData : popupSliderData}
      onClose={() => {}}
      lastNextButton={{
        label: 'Go To Progress',
        onClick: () => {
          navigateToProgress();
        },
      }}
    />
  ) : (
    <></>
  );
};

export default PopupSliderMasteryLevel;
